/*
 * Copyright (c) 2022-2025.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import React, {useCallback, useEffect, useRef, useState} from 'react';

const PRE_ENTER = 0;
const ENTERING = 1;
const ENTERED = 2;
const PRE_EXIT = 3;
const EXITING = 4;
const EXITED = 5;
const UNMOUNTED = 6;
const STATES = ['preEnter', 'entering', 'entered', 'preExit', 'exiting', 'exited', 'unmounted'];

const startOrEnd = (unmounted) => (unmounted ? UNMOUNTED : EXITED);

const updateState = (state, setState, latestState, timeoutId, onChange) => {
    clearTimeout(timeoutId.current);
    setState(state);
    latestState.current = state;
    onChange && onChange({state: STATES[state]});
};

const useTransition = ({
                           enter = true,
                           exit = true,
                           preEnter,
                           preExit,
                           timeout,
                           initialEntered,
                           mountOnEnter,
                           unmountOnExit,
                           onChange
                       } = {}) => {
    const [state, setState] = useState(initialEntered ? ENTERED : startOrEnd(mountOnEnter));
    const latestState = useRef(state);
    const timeoutId = useRef();

    let enterTimeout, exitTimeout;
    if (typeof timeout === 'object') {
        enterTimeout = timeout.enter;
        exitTimeout = timeout.exit;
    } else {
        enterTimeout = exitTimeout = timeout;
    }

    const endTransition = useCallback(() => {
        let newState;
        switch (latestState.current) {
            case ENTERING:
            case PRE_ENTER:
                newState = ENTERED;
                break;

            case EXITING:
            case PRE_EXIT:
                newState = startOrEnd(unmountOnExit);
                break;
        }

        if (newState !== undefined) {
            updateState(newState, setState, latestState, timeoutId, onChange);
        }
    }, [onChange, unmountOnExit]);

    const toggle = useCallback(
        (toEnter) => {
            const transitState = (newState) => {
                updateState(newState, setState, latestState, timeoutId, onChange);

                switch (newState) {
                    case ENTERING:
                        if (enterTimeout >= 0) timeoutId.current = setTimeout(endTransition, enterTimeout);
                        break;

                    case EXITING:
                        if (exitTimeout >= 0) timeoutId.current = setTimeout(endTransition, exitTimeout);
                        break;

                    case PRE_ENTER:
                    case PRE_EXIT:
                        timeoutId.current = setTimeout(() => transitState(newState + 1), 0);
                        break;
                }
            };

            const enterStage = latestState.current <= ENTERED;
            if (typeof toEnter !== 'boolean') toEnter = !enterStage;

            if (toEnter) {
                if (!enterStage) {
                    transitState(enter ? (preEnter ? PRE_ENTER : ENTERING) : ENTERED);
                }
            } else {
                if (enterStage) {
                    transitState(exit ? (preExit ? PRE_EXIT : EXITING) : startOrEnd(unmountOnExit));
                }
            }
        },
        [
            endTransition,
            onChange,
            enter,
            exit,
            preEnter,
            preExit,
            enterTimeout,
            exitTimeout,
            unmountOnExit
        ]
    );

    useEffect(() => () => clearTimeout(timeoutId.current), []);

    return [STATES[state], toggle, endTransition];
};

function Transition(props) {
    const [state, toggle] = useTransition({
        timeout: props.timeout || 500,
        mountOnEnter: props.mountOnEnter ?? true,
        unmountOnExit: props.unmountOnExit ?? true,
        preEnter: props.preEnter ?? false
    });
    useEffect(() => {
        toggle(props.in)
    }, [props.in]);


    const show = state !== "unmounted";

    if (show) {
        return ((typeof props.children === "function") ? (props.children(state)) : (props.children));
    }

    return (<></>);

}


Transition.propTypes = {};

export default Transition;